import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import PropTypes from "prop-types";
import clsx from "clsx";

import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";

import { useAlert } from "react-alert";

import {
  EditSetupLots,
  getFindById,
} from "../../../../adminModel/lots";

import Image from "../../edit/Image";

/* =========================================================
   STYLES
========================================================= */

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },

    "& .MuiInputLabel-outlined": {
      fontSize: 14,
    },

    // Space sa ilalim para hindi matakpan ng fixed footer
    paddingBottom: 80,
  },

  pageCard: {
    borderRadius: 0,
    boxShadow: "none",
    overflow: "visible",
  },

  cardHeader: {
    padding: theme.spacing(2.5, 0),

    "& .MuiCardHeader-title": {
      fontSize: 20,
      fontWeight: 600,
      color: "#222",
    },

    "& .MuiCardHeader-subheader": {
      marginTop: 4,
      fontSize: 13,
      color: "#777",
    },
  },

  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#333",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: theme.spacing(1.5),
  },

  section: {
    marginBottom: theme.spacing(3),
  },

  field: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },

  imageWrapper: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    border: "1px solid #e5e5e5",
    borderRadius: 6,
    backgroundColor: "#fafafa",
  },

  /*
   * ========================================================
   * FIXED SAVE FOOTER
   * ========================================================
   */

  actionWrapper: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,

    zIndex: 1200,

    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",

    padding: theme.spacing(1.5, 3),

    backgroundColor: "rgba(255, 255, 255, 0.97)",

    borderTop: "1px solid #e0e0e0",

    boxShadow:
      "0 -2px 10px rgba(0, 0, 0, 0.10)",

    backdropFilter: "blur(4px)",

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 2),
    },
  },

  saveButton: {
    minWidth: 150,
    height: 42,

    fontWeight: 600,
    textTransform: "none",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
      minWidth: 0,
    },
  },

  backdrop: {
    zIndex: 9999,
    color: "#fff",
  },

  divider: {
    margin: theme.spacing(2, 0),
  },

  priceField: {
    "& input": {
      fontWeight: 600,
    },
  },
}));

/* =========================================================
   INITIAL VALUES
========================================================= */

const initialValues = {
  lot_number: "",
  artist: "",
  title: "",
  starting_price: "",
  signed_dated: "",
  medium: "",
  size: "",
  property: "",
  coa: "",
  provenance: "",
  exhibited: "",
  literature: "",
  write_up: "",
  lot_group: "",
  lot_details_html: "",
  alias: "",
  hp: "",
  reserve: "",
  bp: "",
  accounting_hp: 0,
};

/* =========================================================
   COMPONENT
========================================================= */

const LotDetails = ({
  eventId,
  lotId,
  something,
  className,
  ...rest
}) => {
  const classes = useStyles();
  const alert = useAlert();

  const [loading, setLoading] = useState(false);
  const [getDetails, setDetails] = useState([]);
  const [values, setValues] = useState(initialValues);

  /* =======================================================
     FETCH LOT DETAILS
  ======================================================= */

  const FetchAuction = useCallback(async () => {
    if (!eventId || !lotId || !something) {
      return;
    }

    setLoading(true);

    try {
      const response = await getFindById(
        eventId,
        lotId,
        something
      );

      setDetails(response?.image || []);

      let results = {};

      if (
        response?.data &&
        response.data.length > 0
      ) {
        results =
          response.data[
            response.data.length - 1
          ];
      }

      setValues({
        lot_number:
          results.lot_number || "",

        artist:
          results.artist || "",

        title:
          results.title || "",

        starting_price:
          results.starting_price || "",

        signed_dated:
          results.signed_dated || "",

        medium:
          results.medium || "",

        size:
          results.size || "",

        property:
          results.property || "",

        coa:
          results.coa || "",

        provenance:
          results.provenance || "",

        exhibited:
          results.exhibited || "",

        literature:
          results.literature || "",

        write_up:
          results.write_up || "",

        lot_group:
          results.lot_group || "",

        lot_details_html:
          results.lot_details_html || "",

        alias:
          results.alias || "",

        hp:
          results.hp || "",

        reserve:
          results.reserve_price || "",

        bp:
          results.bp || "",

        accounting_hp:
          results.accounting_hp || 0,
      });
    } catch (error) {
      console.error(
        "Failed to load lot details:",
        error
      );

      alert.error(
        "Failed to load lot details"
      );
    } finally {
      setLoading(false);
    }
  }, [
    eventId,
    lotId,
    something,
    alert,
  ]);

  useEffect(() => {
    FetchAuction();
  }, [FetchAuction]);

  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =======================================================
     SUBMIT
  ======================================================= */

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    setLoading(true);

    const lotsData = {
      auction_id: eventId,

      set_id: lotId,

      lot_number: something,

      artist:
        values.artist,

      title:
        values.title,

      starting_price:
        values.starting_price,

      signed_dated:
        values.signed_dated,

      medium:
        values.medium,

      size:
        values.size,

      property:
        values.property,

      coa:
        values.coa,

      provenance:
        values.provenance,

      exhibited:
        values.exhibited,

      literature:
        values.literature,

      write_up:
        values.write_up,

      lot_group:
        values.lot_group,

      lot_details_html:
        values.lot_details_html,

      alias:
        values.alias,

      hp:
        parseInt(values.hp),

      reserve: isNaN(
        parseInt(values.reserve)
      )
        ? 0
        : parseInt(values.reserve),

      bp:
        values.bp,

      accounting_hp:
        values.accounting_hp,
    };

    try {
      const result =
        await EditSetupLots(
          lotsData
        );

      alert.success(result);
    } catch (error) {
      console.error(
        "Update lot error:",
        error
      );

      alert.error(
        "Failed to update lot"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Box
      className={clsx(
        classes.root,
        className
      )}
      {...rest}
    >
      <Grid container spacing={3}>

        <Grid item xs={12}>

          <form
            onSubmit={handleFormSubmit}
            autoComplete="off"
          >

            <Card
              className={
                classes.pageCard
              }
            >

              {/* =================================================
                  HEADER
              ================================================= */}

              <CardHeader
                className={
                  classes.cardHeader
                }
                title="Lot Details"
                subheader={
                  values.title
                    ? `Editing: ${values.title}`
                    : "Edit lot information"
                }
              />

              <Divider />

              {/* =================================================
                  CONTENT
              ================================================= */}

              <CardContent>

                <Grid
                  container
                  spacing={4}
                >

                  {/* =================================================
                      LEFT COLUMN
                  ================================================= */}

                  <Grid
                    item
                    xs={12}
                    md={6}
                  >

                    <Typography
                      className={
                        classes.sectionTitle
                      }
                    >
                      Basic Information
                    </Typography>

                    {/* LOT NUMBER */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      label="Lot Number"
                      name="lot_number"
                      type="number"
                      variant="outlined"
                      value={
                        values.lot_number
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* ARTIST */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      multiline
                      minRows={2}
                      label="Artist"
                      name="artist"
                      variant="outlined"
                      value={
                        values.artist
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* TITLE */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      multiline
                      minRows={2}
                      label="Title"
                      name="title"
                      variant="outlined"
                      value={
                        values.title
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* SIGNED / DATED */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      multiline
                      minRows={2}
                      label="Signed & Dated"
                      name="signed_dated"
                      variant="outlined"
                      value={
                        values.signed_dated
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* MEDIUM */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      multiline
                      minRows={2}
                      label="Medium"
                      name="medium"
                      variant="outlined"
                      value={
                        values.medium
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* SIZE */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      multiline
                      minRows={2}
                      label="Size"
                      name="size"
                      variant="outlined"
                      value={
                        values.size
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* LOT GROUP */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      label="Lot Group"
                      name="lot_group"
                      variant="outlined"
                      value={
                        values.lot_group
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* BUYER'S PREMIUM */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      label="Buyer's Premium"
                      name="bp"
                      variant="outlined"
                      value={
                        values.bp
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* IMAGE */}

                    <Box
                      className={
                        classes.imageWrapper
                      }
                    >

                      <Typography
                        variant="subtitle2"
                        style={{
                          marginBottom: 12,
                          fontWeight: 600,
                        }}
                      >
                        Lot Images
                      </Typography>

                      <Image
                        ItemData={
                          getDetails
                        }
                      />

                    </Box>

                  </Grid>

                  {/* =================================================
                      RIGHT COLUMN
                  ================================================= */}

                  <Grid
                    item
                    xs={12}
                    md={6}
                  >

                    <Typography
                      className={
                        classes.sectionTitle
                      }
                    >
                      Pricing & Details
                    </Typography>

                    {/* HAMMER PRICE */}

                    <TextField
                      className={`${classes.field} ${classes.priceField}`}
                      fullWidth
                      label="Hammer Price"
                      name="hp"
                      variant="outlined"
                      value={
                        values.hp
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* ACCOUNTING HAMMER PRICE */}

                    <TextField
                      className={`${classes.field} ${classes.priceField}`}
                      fullWidth
                      label="Accounting Hammer Price"
                      name="accounting_hp"
                      variant="outlined"
                      value={
                        values.accounting_hp
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* STARTING PRICE */}

                    <TextField
                      className={`${classes.field} ${classes.priceField}`}
                      fullWidth
                      label="Starting Price"
                      name="starting_price"
                      type="number"
                      variant="outlined"
                      value={
                        values.starting_price
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* RESERVE */}

                    <TextField
                      className={`${classes.field} ${classes.priceField}`}
                      fullWidth
                      label="Reserve"
                      name="reserve"
                      variant="outlined"
                      value={
                        values.reserve
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* PROPERTY */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      label="Property"
                      name="property"
                      variant="outlined"
                      value={
                        values.property
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* COA */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      multiline
                      minRows={4}
                      label="COA"
                      name="coa"
                      variant="outlined"
                      value={
                        values.coa
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* EXHIBITED */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      multiline
                      minRows={4}
                      label="Exhibited"
                      name="exhibited"
                      variant="outlined"
                      value={
                        values.exhibited
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* ALIAS */}

                    <TextField
                      className={
                        classes.field
                      }
                      fullWidth
                      multiline
                      minRows={4}
                      label="Alias"
                      name="alias"
                      variant="outlined"
                      value={
                        values.alias
                      }
                      onChange={
                        handleChange
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                  </Grid>

                  {/* =================================================
                      FULL WIDTH DETAILS
                  ================================================= */}

                  <Grid
                    item
                    xs={12}
                  >

                    <Divider
                      className={
                        classes.divider
                      }
                    />

                    <Typography
                      className={
                        classes.sectionTitle
                      }
                    >
                      Additional Information
                    </Typography>

                    <Grid
                      container
                      spacing={3}
                    >

                      {/* PROVENANCE */}

                      <Grid
                        item
                        xs={12}
                        md={6}
                      >

                        <TextField
                          fullWidth
                          multiline
                          minRows={5}
                          label="Provenance"
                          name="provenance"
                          variant="outlined"
                          value={
                            values.provenance
                          }
                          onChange={
                            handleChange
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />

                      </Grid>

                      {/* LITERATURE */}

                      <Grid
                        item
                        xs={12}
                        md={6}
                      >

                        <TextField
                          fullWidth
                          multiline
                          minRows={5}
                          label="Literature"
                          name="literature"
                          variant="outlined"
                          value={
                            values.literature
                          }
                          onChange={
                            handleChange
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />

                      </Grid>

                      {/* WRITE UP */}

                      <Grid
                        item
                        xs={12}
                        md={6}
                      >

                        <TextField
                          fullWidth
                          multiline
                          minRows={5}
                          label="Write Up"
                          name="write_up"
                          variant="outlined"
                          value={
                            values.write_up
                          }
                          onChange={
                            handleChange
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />

                      </Grid>

                      {/* LOT DETAILS HTML */}

                      <Grid
                        item
                        xs={12}
                        md={6}
                      >

                        <TextField
                          fullWidth
                          multiline
                          minRows={5}
                          label="Lot Details HTML"
                          name="lot_details_html"
                          variant="outlined"
                          value={
                            values.lot_details_html
                          }
                          onChange={
                            handleChange
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />

                      </Grid>

                    </Grid>

                  </Grid>

                </Grid>

              </CardContent>

            </Card>

            {/* =================================================
                FIXED SAVE FOOTER
            ================================================= */}

            <Box
              className={
                classes.actionWrapper
              }
            >

              <Button
                className={
                  classes.saveButton
                }
                color="primary"
                variant="contained"
                type="submit"
                disabled={loading}
              >
                {loading
                  ? "Saving..."
                  : "Save Details"}
              </Button>

            </Box>

          </form>

        </Grid>

      </Grid>

      {/* =================================================
          LOADING
      ================================================= */}

      <Backdrop
        className={
          classes.backdrop
        }
        open={loading}
      >
        <CircularProgress
          color="inherit"
        />
      </Backdrop>

    </Box>
  );
};

/* =========================================================
   PROP TYPES
========================================================= */

LotDetails.propTypes = {
  className: PropTypes.string,

  eventId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),

  lotId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),

  something: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

/* =========================================================
   EXPORT
========================================================= */

export default LotDetails;